import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Award, 
  Plus, 
  Eye,
  Calendar,
  Printer
} from 'lucide-react';
import { createCertificate, getCertificates } from '@/services/api';
import { toast } from 'sonner';

const certificateTypes = [
  { value: 'sick_leave', label: 'Sick Leave Certificate' },
  { value: 'fitness', label: 'Fitness Certificate' },
  { value: 'medical_clearance', label: 'Medical Clearance' }
];

export default function CertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  
  // Form states
  const [patientName, setPatientName] = useState('');
  const [certificateType, setCertificateType] = useState('sick_leave');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [remarks, setRemarks] = useState('');
  const [doctorName, setDoctorName] = useState('');
  
  const printRef = useRef(null);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const data = await getCertificates();
      setCertificates(data);
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!patientName.trim() || !startDate || !doctorName.trim()) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      await createCertificate({
        patient_name: patientName,
        certificate_type: certificateType,
        start_date: startDate,
        end_date: endDate || null,
        diagnosis: diagnosis || null,
        remarks: remarks || null,
        doctor_name: doctorName
      });
      toast.success('Certificate created successfully!');
      setDialogOpen(false);
      resetForm();
      await loadCertificates();
    } catch (error) {
      console.error('Create certificate error:', error);
      toast.error('Failed to create certificate');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setPatientName('');
    setCertificateType('sick_leave');
    setStartDate('');
    setEndDate('');
    setDiagnosis('');
    setRemarks('');
    setDoctorName('');
  };

  const viewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setViewDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Medical Certificate</title>');
    printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:40px;}.header{text-align:center;border-bottom:2px solid #1e3a8a;padding-bottom:20px;margin-bottom:30px;}.content{line-height:1.8;}.field{margin:15px 0;}.label{font-weight:bold;}.footer{margin-top:60px;text-align:right;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const getCertificateLabel = (type) => {
    return certificateTypes.find(t => t.value === type)?.label || type;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="certificates-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Medical Certificates
            </h1>
            <p className="text-slate-600 mt-1">Generate and manage medical certificates</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="create-certificate-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Certificate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Certificate</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="patientName">Patient Name *</Label>
                  <Input
                    id="patientName"
                    placeholder="Enter patient name"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    data-testid="cert-patient-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="certificateType">Certificate Type *</Label>
                  <Select value={certificateType} onValueChange={setCertificateType}>
                    <SelectTrigger id="certificateType" data-testid="cert-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {certificateTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">From Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="cert-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">To Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="cert-end-date-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="diagnosis">Diagnosis/Condition</Label>
                  <Input
                    id="diagnosis"
                    placeholder="Enter diagnosis"
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    data-testid="cert-diagnosis-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doctorName">Doctor Name *</Label>
                  <Input
                    id="doctorName"
                    placeholder="Dr. "
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    data-testid="cert-doctor-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="remarks">Remarks</Label>
                  <Textarea
                    id="remarks"
                    placeholder="Additional remarks..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    data-testid="cert-remarks-input"
                  />
                </div>

                <Button
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="submit-certificate-btn"
                >
                  {creating ? 'Creating...' : 'Generate Certificate'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Certificates List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : certificates.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No certificates yet</p>
              <p className="text-sm text-slate-400 mt-1">Generate your first medical certificate</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {certificates.map((certificate, index) => (
              <Card 
                key={certificate.id} 
                className="bg-white border-slate-200 shadow-sm card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-amber-700" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900">{certificate.patient_name}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs">
                            {getCertificateLabel(certificate.certificate_type)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {certificate.start_date}
                            {certificate.end_date && ` - ${certificate.end_date}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewCertificate(certificate)}
                      data-testid={`view-certificate-${certificate.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Certificate Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            {selectedCertificate && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Medical Certificate</DialogTitle>
                    <Button variant="outline" size="sm" onClick={handlePrint}>
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                  </div>
                </DialogHeader>
                
                <div ref={printRef} className="space-y-6 mt-4">
                  <div className="header text-center border-b-2 border-blue-900 pb-4">
                    <h2 className="text-2xl font-bold text-blue-900">MEDICAL CERTIFICATE</h2>
                    <p className="text-lg mt-2">{getCertificateLabel(selectedCertificate.certificate_type)}</p>
                    <p className="text-sm text-slate-500 mt-1">Issue Date: {selectedCertificate.issue_date}</p>
                  </div>

                  <div className="content space-y-4 text-slate-700">
                    <p>This is to certify that:</p>
                    
                    <div className="field p-4 bg-slate-50 rounded-lg">
                      <span className="label">Patient Name: </span>
                      <span className="font-semibold">{selectedCertificate.patient_name}</span>
                    </div>

                    {selectedCertificate.diagnosis && (
                      <div className="field">
                        <span className="label">Diagnosis/Condition: </span>
                        <span>{selectedCertificate.diagnosis}</span>
                      </div>
                    )}

                    <div className="field">
                      <p>Has been advised rest/treatment for the following period:</p>
                      <p className="font-semibold mt-2">
                        From: {selectedCertificate.start_date}
                        {selectedCertificate.end_date && (
                          <span> To: {selectedCertificate.end_date}</span>
                        )}
                      </p>
                    </div>

                    {selectedCertificate.remarks && (
                      <div className="field">
                        <span className="label">Remarks: </span>
                        <span>{selectedCertificate.remarks}</span>
                      </div>
                    )}
                  </div>

                  <div className="footer text-right pt-8 border-t mt-8">
                    <div className="inline-block text-left">
                      <p className="font-semibold text-lg">{selectedCertificate.doctor_name}</p>
                      <p className="text-sm text-slate-500">Authorized Signature</p>
                      <p className="text-sm text-slate-500 mt-4">Date: {selectedCertificate.issue_date}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
